/**
 * Wander Brand Icons
 *
 * Inline SVG components matching the brand icon set in /icons/.
 * Primary stroke color: #1d8e85 (Deep Jade — brand primary).
 *
 * Each icon accepts `size` (default 20), `className`, and an optional
 * `color` override so we can render them white-on-teal when selected.
 */

import React from 'react';

const BRAND_TEAL = '#1d8e85'; // Deep Jade (brand primary)

// ---------------------------------------------------------------------------
// Heart – /icons/icon-heart.svg
// ---------------------------------------------------------------------------
export const BrandHeart = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Sparkles – /icons/icon-sparkles.svg  (multi-tone brand sparkle)
// ---------------------------------------------------------------------------
export const BrandSparkles = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    {...props}
  >
    <path d="M12 3L13.5 8.5L19 10L13.5 11.5L12 17L10.5 11.5L5 10L10.5 8.5L12 3Z" fill={color} />
    <path d="M19 14L19.75 16.25L22 17L19.75 17.75L19 20L18.25 17.75L16 17L18.25 16.25L19 14Z" fill={color} opacity="0.6" />
    <path d="M5 14L5.5 15.5L7 16L5.5 16.5L5 18L4.5 16.5L3 16L4.5 15.5L5 14Z" fill={color} opacity="0.8" />
  </svg>
);

// ---------------------------------------------------------------------------
// Compass – /icons/icon-compass.svg
// ---------------------------------------------------------------------------
export const BrandCompass = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

// ---------------------------------------------------------------------------
// Chat – /icons/icon-chat.svg
// ---------------------------------------------------------------------------
export const BrandChat = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Map – /icons/icon-map.svg
// ---------------------------------------------------------------------------
export const BrandMap = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" />
    <line x1="16" y1="6" x2="16" y2="22" />
  </svg>
);

// ---------------------------------------------------------------------------
// Clock – /icons/icon-clock.svg
// ---------------------------------------------------------------------------
export const BrandClock = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ---------------------------------------------------------------------------
// Cards/Grid – custom icon for card view (no direct brand SVG, but teal-styled)
// ---------------------------------------------------------------------------
export const BrandCards = ({ size = 20, color = BRAND_TEAL, className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
